import { useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { UserAuditLogsDocument } from '../graphql/types';

const FIRST = 25;

const useUserAuditLogsQuery = (userId: string | undefined) => {
  // "loading" will be true whenever the query hook is executed
  // but we want a loading state that is specific to when
  // `fetchMore` is executed, and this doesn't seem
  // to be something apollo client provides
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data, loading, error, fetchMore, refetch } = useQuery(
    UserAuditLogsDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        id: userId!, // see skip
        input: {
          first: FIRST,
        },
      },
      skip: !userId,
    },
  );

  const logs = data?.user?.auditLogs.edges?.map((edge) => edge.node);
  const logCount = data?.user?.auditLogs.count;

  const hasNextPage = data?.user?.auditLogs.pageInfo.hasNextPage;

  const loadMore = async () => {
    if (!hasNextPage || isFetchingMore) {
      return;
    }

    const endCursor = data?.user?.auditLogs.pageInfo.endCursor;

    setIsFetchingMore(true);

    try {
      await fetchMore({
        variables: {
          id: userId,
          input: { first: FIRST, after: endCursor },
        },
      });
    } finally {
      setIsFetchingMore(false);
    }
  };

  return {
    data,
    loadMore,
    logCount,
    logs,
    fetch,
    refetch,
    error,
    loading,
    hasNextPage,
    isFetchingMore,
  };
};

export default useUserAuditLogsQuery;
